import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  CardMedia,
  Grid2,
  Divider,
  Menu,
  MenuItem,
  TextField,
  IconButton,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import PublishIcon from "@mui/icons-material/Publish";
import DeleteIcon from "@mui/icons-material/Delete";
import NotInterestedIcon from "@mui/icons-material/NotInterested";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlaceholderImage from "@mui/icons-material/Image";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  fetchStoryDetails,
  deleteChapter,
  editStory,
  togglePublishStory,
  deleteStory,
  toggleActiveStatus,
} from "../utils/api";
import { cropAndResizeImage } from "../components/StoryForm";
import { useTheme } from "@mui/material/styles";
import ChapterList from "../components/ChapterList";
import { t } from "@lingui/macro";

const SkeletonStoryDetails = () => {
  const theme = useTheme();
  return (
    <Box sx={{ maxWidth: 800, margin: "auto", padding: theme.spacing(3) }}>
      {/* Skeleton for Cover and Title */}
      <Box display="flex" alignItems="center" gap={2}>
        <Skeleton variant="rectangular" width={140} height={200} />
        <Box flex={1}>
          <Skeleton variant="text" width="60%" height={40} />
          <Skeleton variant="text" width="80%" height={20} />
          <Skeleton variant="text" width="40%" height={20} />
        </Box>
      </Box>

      <Divider sx={{ marginY: theme.spacing(3) }} />

      {/* Skeleton for Chapters */}
      <Skeleton variant="text" width="30%" height={30} />
      {Array.from(new Array(3)).map((_, index) => (
        <Skeleton
          key={index}
          variant="rectangular"
          width="100%"
          height={60}
          sx={{ mt: 2, borderRadius: 2 }}
        />
      ))}

      {/* Skeleton for Add Chapter Button */}
      <Skeleton
        variant="rectangular"
        width="100%"
        height={50}
        sx={{ mt: 3, borderRadius: 2 }}
      />
    </Box>
  );
};

const StoryDetailsPage = () => {
  const { storyID } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);

  // Editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editCover, setEditCover] = useState("");
  const [editTags, setEditTags] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [editedCoverFile, setEditedCoverFile] = useState(null);
  const [isCoverMissing, setIsCoverMissing] = useState(false);
  const [isCoverInappropriate, setIsCoverInappropriate] = useState(false);
  const [isDragging, setIsDragging] = useState(false);


  // Saving state for save button
  const [saving, setSaving] = useState(false);
  // Publishing state for toggling publish/unpublish
  const [publishing, setPublishing] = useState(false);
  const [hasPublishedChapters, setHasPublishedChapters] = useState(false);
  const [hasDelistedChapters, setHasDelistedChapters] = useState(false);
  const [fetchingStory, setFetchingStory] = useState(false);

  // Active status toggle state
  const [togglingActive, setTogglingActive] = useState(false);

  // Delete story dialog state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [confirmDeletion, setConfirmDeletion] = useState(false);
  const [deleteCountdown, setDeleteCountdown] = useState(5);
  const [deleting, setDeleting] = useState(false);

  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // File input ref for cover image editing
  const inputRef = useRef(null);

  // Predefined categories/tags
  const categories = [t`Romantik`, t`Fantastik`, t`Macera`, t`Dram`, t`Gençlik`, t`Bilim Kurgu`, t`Smut`];

  useEffect(() => {
    fetchStoryDetails(storyID)
      .then((data) => {
        setStory(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
      });
  }, [storyID]);

  // Initialize edit fields when story loads
  useEffect(() => {
    if (story && !isEditing) {
      setEditTitle(story.title);
      setEditDescription(story.description);
      setEditCover(story.coverImageUrl);
      setEditTags(story.tags || []);
    }
  }, [story, isEditing]);

  useEffect(() => {
    if (story?.chapters) {
      const hasPublished = Object.values(story.chapters).some(
        (chapter) => chapter.published === true
      );
      setHasPublishedChapters(hasPublished);
    }
  }, [story?.chapters]);

  useEffect(() => {
    if (story?.chapters) {
      const hasDelisted = Object.values(story.chapters).some(
        (chapter) => chapter.delisted === true
      );
      setHasDelistedChapters(hasDelisted);
    }
  }, [story?.chapters]);

  const handleDeleteChapter = (chapterIndex) => {
    return deleteChapter(storyID, chapterIndex).then(() => {
      setStory((prev) => {
        const updatedChapters = { ...prev.chapters };
        delete updatedChapters[chapterIndex];

        // Check if any published chapters remain
        const hasPublished = Object.values(updatedChapters).some(
          (chapter) => chapter.published === true
        );

        return {
          ...prev,
          chapters: updatedChapters,
          isPublished: hasPublished, // ✅ Update story publish status
        };
      });

      // ✅ Also update hasPublishedChapters state
      setHasPublishedChapters((prevStory) => {
        return Object.values(prevStory.chapters || {}).some(
          (chapter) => chapter.published === true
        );
      });
    });
  };

  const handleToggleActiveStatus = async (event, newStatus) => {
    if (newStatus === null) return; // Do nothing if no change
    setTogglingActive(true);
    try {
      // Assume toggleActiveStatus(storyID, isActive) returns { isActive }
      const result = await toggleActiveStatus(storyID, newStatus === "active");
      setStory((prev) => ({ ...prev, isActive: result.isActive }));
    } catch (error) {
      console.error("Error toggling active status", error);
      // Optionally display an error message via Snackbar, etc.
    } finally {
      setTogglingActive(false);
    }
  };

  useEffect(() => {
    if (menuAnchorEl) {
      // Close and reopen menu to reflect latest `isPublished`
      handleCloseMenu();
      setTimeout(() => {
        setMenuAnchorEl(menuAnchorEl);
      }, 100);
    }
  }, [story?.isPublished]);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];

      // Ensure it's an image
      if (!file.type.startsWith("image/")) {
        console.warn("Dropped file is not an image.");
        return;
      }

      handleCoverChange({ target: { files: [file] } });

      // Clear files from dataTransfer to prevent issues on macOS
      e.dataTransfer.clearData();
    } else {
      console.warn("No files detected in drop event.");
    }
  };

  const handleAddChapter = () => {
    const newIndex = Object.keys(story.chapters).length + 1;
    navigate(
      `/mystories/${storyID}/${newIndex}`,
      {
        state: {
          isNewChapter: true,      // <–– this tells the editor “don’t fetch old content”
        },
      }
    );
  };
  

  // Menu handlers
  const handleOpenMenu = async (event) => {
    setMenuAnchorEl(event.currentTarget);
    setFetchingStory(true);

    try {
      const updatedStory = await fetchStoryDetails(storyID);
      setStory(updatedStory);
    } catch (error) {
      console.error(t`Error refetching story details:`, error);
    } finally {
      setFetchingStory(false);
    }
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleEdit = () => {
    setIsEditing(true);
    handleCloseMenu();
  };

  const handleTogglePublishStory = async () => {
    setPublishing(true);
    try {
      const result = await togglePublishStory(storyID);

      setStory((prev) => ({
        ...prev,
        isPublished: result.isPublished,
      }));

      const publishStatus = result.isPublished ? t`published` : t`unpublished`;
      setSnackbarMessage(t`Story ${publishStatus} successfully.`);
      setSnackbarOpen(true);
    } catch (error) {
      console.error(t`Error toggling publish status:`, error);

      if (error.message.includes("cover image is missing")) {
        setIsEditing(true);
        setIsCoverMissing(true);
        setSnackbarMessage(t`Cover image is required to publish the story. Please add one.`);
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage(t`Error toggling publish status: ${error.message}`);
        setSnackbarOpen(true);
      }
    } finally {
      setPublishing(false);
      handleCloseMenu();
    }
  };

  const handleChapterPublished = async (chapterIndex) => {
    if (chapterIndex === "MISSING_COVER") {
      setIsEditing(true);
      setIsCoverMissing(true);
      return;
    }

    if (menuAnchorEl) {
      setFetchingStory(true);
      try {
        const updatedStory = await fetchStoryDetails(storyID);
        setStory(updatedStory);
      } catch (error) {
        console.error(t`Error refetching story details:`, error);
      } finally {
        setFetchingStory(false);
      }
    } else {
      setStory((prevStory) => {
        if (!prevStory || !prevStory.chapters) return prevStory;
        // Get indexes of already published chapters as numbers
        const publishedIndexes = Object.keys(prevStory.chapters)
          .filter((idx) => prevStory.chapters[idx].published)
          .map((idx) => parseInt(idx, 10));
        const currentMax = publishedIndexes.length > 0 ? Math.max(...publishedIndexes) : 0;
        // Determine if the new chapter is "new"
        const newIsActive = parseInt(chapterIndex, 10) > currentMax - 1;
        return {
          ...prevStory,
          isPublished: true,
          isActive: newIsActive,
          chapters: {
            ...prevStory.chapters,
            [chapterIndex]: {
              ...prevStory.chapters[chapterIndex],
              status: "PUBLISHED",
              published: true,
              delisted: false,
            },
          },
        };
      });
    }
  };


  const handleOpenDeleteDialog = () => {
    setOpenDeleteDialog(true);
    setDeleteCountdown(5);
    handleCloseMenu();
  };

  const handleCancelDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setConfirmDeletion(false);
  };

  useEffect(() => {
    let timer;
    if (openDeleteDialog) {
      timer = setInterval(() => {
        setDeleteCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [openDeleteDialog]);

  const triggerDeleteStory = async () => {
    setDeleting(true);
    try {
      const result = await deleteStory(storyID);
      setSnackbarMessage(t`Story deleted successfully. Redirecting...`);
      setSnackbarOpen(true);
      setTimeout(() => {
        navigate("/mystories");
      }, 2000);
    } catch (error) {
      console.error(t`Error deleting story:`, error);
      setSnackbarMessage(t`Error deleting story.`);
      setSnackbarOpen(true);
    } finally {
      setDeleting(false);
      setOpenDeleteDialog(false);
    }
  };

  const effectiveIsActive = story?.isActive ?? true;


  const handleSave = async () => {
    setSaving(true);
    try {
      const updatedStory = await editStory({
        storyID,
        title: editTitle,
        description: editDescription,
        cover: editedCoverFile ? editedCoverFile : undefined,
        categories: editTags,
      });

      const newStoryData = updatedStory.story;
      setStory((prevStory) => ({
        ...prevStory,
        title: newStoryData.title,
        description: newStoryData.description,
        coverImageUrl: newStoryData.coverImageUrl || editCover,
        tags: newStoryData.tags,
        chapters: newStoryData.chapters || prevStory.chapters,
      }));
      setIsEditing(false);
      setHasChanges(false);
      setIsCoverInappropriate(false);
      setSnackbarMessage(t`Changes saved!`);
      setSnackbarOpen(true);
    } catch (error) {
      console.error(t`Error editing story:`, error);
      setSnackbarMessage(error.message || t`Error saving changes.`);
      setSnackbarOpen(true);
      if (error.message.includes("Cover Image")) {
        setIsCoverInappropriate(true);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCoverChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      cropAndResizeImage(file, 354, 500)
        .then((resizedFile) => {
          setEditCover(URL.createObjectURL(resizedFile));
          setEditedCoverFile(resizedFile);
          setHasChanges(true);
          setIsCoverMissing(false);
        })
        .catch((err) => {
          console.error(t`Image processing failed:`, err);
        });
    }
  };

  const handleTagChange = (event, newTags) => {
    if (newTags.length > 2) {
      newTags = newTags.slice(-2);
    }
    setEditTags(newTags);
    setHasChanges(true);
  };

  if (loading) return <SkeletonStoryDetails />;

  return (
    <Box sx={{ maxWidth: 800, margin: "auto", padding: theme.spacing(3) }}>
      {/* Delete confirmation dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCancelDeleteDialog}>
        <DialogTitle>{t`Confirm Story Deletion`}</DialogTitle>
        <DialogContent>
          <Typography>
            {t`Are you sure you want to delete this story? This action cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeleteDialog} color="primary">
            {t`Cancel`}
          </Button>
          <Button
            onClick={triggerDeleteStory}
            color="error"
            disabled={deleteCountdown > 0 || deleting}
          >
            {deleteCountdown > 0
              ? t`Delete (${deleteCountdown})`
              : t`Delete`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cover/Title/Description Block */}
      <Box sx={{ position: "relative" }}>
        <Grid2 container spacing={2} alignItems="center" sx={{ position: "relative" }}>

          <Grid2 item xs={4}>
            {!isEditing && (
              <Box
                sx={{
                  position: "relative",
                  top: 40,
                  right: -156, // Keeps the box positioned slightly outside the cover
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 1, // Space between the two status rows
                }}
              >
                {/* Published/Draft Status */}
                <Box display="flex" alignItems="center">
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: story?.isPublished ? "green" : "gold",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      ml: 1,
                      fontSize: 12,
                      fontWeight: "bold",
                      color: theme.palette.text.primary,
                      textTransform: "uppercase",
                    }}
                  >
                    {story?.isPublished ? t`PUBLISHED` : t`DRAFT`}
                  </Typography>
                </Box>

                {/* Active/Finished Status */}
                <Box display="flex" alignItems="center">
                  {effectiveIsActive ? (
                    <EditIcon sx={{ color: "gold", fontSize: 14 }} />
                  ) : (
                    <CheckCircleIcon sx={{ color: "green", fontSize: 14 }} />
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      ml: 1,
                      fontSize: 12,
                      fontWeight: "bold",
                      color: theme.palette.text.primary,
                      textTransform: "uppercase",
                    }}
                  >
                    {effectiveIsActive ? t`TO BE CONTINUED...` : t`FINISHED`}
                  </Typography>
                </Box>
              </Box>
            )}

            <Box
              sx={{
                position: "relative",
                width: 140,
                height: 200,
                borderRadius: "4px",
                boxShadow: theme.shadows[3],
                overflow: "hidden",
                cursor: isEditing ? "pointer" : "default",
                border: isEditing
                  ? isCoverMissing || isCoverInappropriate
                    ? "2px dashed red"
                    : `2px dashed ${isDragging ? "gold" : theme.palette.text.primary}`
                  : "none",
                backgroundColor: theme.palette.grey[200],
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                color: theme.palette.grey[500],
              }}
              onClick={isEditing ? () => inputRef.current.click() : undefined}
              onDragEnter={isEditing ? handleDragEnter : undefined}
              onDragOver={isEditing ? handleDragOver : undefined}
              onDragLeave={isEditing ? handleDragLeave : undefined}
              onDrop={isEditing ? handleDrop : undefined}
            >

              {editCover ? (
                <CardMedia
                  component="img"
                  image={editCover}
                  alt={t`Story Cover`}
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : isEditing ? (
                <>
                  <PlaceholderImage sx={{ fontSize: 64 }} />
                  <Typography variant="body2" align="center" color="textSecondary">
                    {t`Drag and Drop or Click to Upload`}
                  </Typography>
                </>
              ) : (
                <>
                  <PlaceholderImage sx={{ fontSize: 64 }} />
                  <Typography variant="body2" align="center" color="textSecondary">
                    {t`Story Cover`}
                  </Typography>
                </>
              )}
              <input
                type="file"
                accept="image/*"
                ref={inputRef}
                style={{ display: "none" }}
                onChange={handleCoverChange}
              />
            </Box>

          </Grid2>

          <Grid2 item xs={8}>
            {isEditing ? (
              <>
                <TextField
                  fullWidth
                  value={editTitle}
                  onChange={(e) => {
                    setEditTitle(e.target.value);
                    setHasChanges(true);
                  }}
                  variant="standard"
                  inputProps={{ maxLength: 40 }}
                  helperText={`${editTitle.length}/40`}
                  error={editTitle.length < 1}
                  sx={{ mb: 2 }}
                  placeholder={t`Title`}
                  InputProps={{
                    style: {
                      fontSize: theme.typography.h4.fontSize,
                      fontWeight: theme.typography.h4.fontWeight,
                      fontFamily: theme.typography.h4.fontFamily,
                      lineHeight: theme.typography.h4.lineHeight,
                    },
                  }}
                />
                <TextField
                  fullWidth
                  multiline
                  value={editDescription}
                  onChange={(e) => {
                    setEditDescription(e.target.value);
                    setHasChanges(true);
                  }}
                  variant="standard"
                  inputProps={{ maxLength: 400 }}
                  helperText={t`${editDescription.length}/400 (min 100 required)`}
                  error={editDescription.length > 0 && editDescription.length < 100}
                  placeholder={t`Description`}
                  InputProps={{
                    style: {
                      fontSize: theme.typography.body1.fontSize,
                      fontFamily: theme.typography.body1.fontFamily,
                      lineHeight: theme.typography.body1.lineHeight,
                    },
                  }}
                />
                <Box sx={{ mt: theme.spacing(2) }}>
                  <Typography sx={{ mb: theme.spacing(2) }} variant="body1">
                    {t`Select Tags (1-2):`}
                  </Typography>
                  <ToggleButtonGroup
                    value={editTags}
                    onChange={handleTagChange}
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: theme.spacing(1),
                      justifyContent: "center",
                    }}
                  >
                    {categories.map((category) => (
                      <ToggleButton
                        key={category}
                        value={category}
                        sx={{
                          textTransform: "none",
                          fontWeight: "bold",
                          fontSize: "0.9rem",
                          minWidth: "80px",
                          height: "40px",
                          transition: "all 0.2s ease-in-out",
                          backgroundColor: editTags.includes(category)
                            ? theme.palette.callToAction.main
                            : theme.palette.background.paper,
                          color: editTags.includes(category)
                            ? theme.palette.callToAction.contrastText
                            : theme.palette.text.primary,
                          border: `2px solid ${theme.palette.callToAction.main}`,
                          borderRadius: "8px !important",
                          "&:hover": {
                            backgroundColor: editTags.includes(category)
                              ? theme.palette.callToAction.dark
                              : theme.palette.action.hover,
                            borderColor: theme.palette.callToAction.dark,
                          },
                          "&.Mui-selected": {
                            backgroundColor: theme.palette.callToAction.main,
                            color: theme.palette.callToAction.contrastText,
                            border: `2px solid ${theme.palette.callToAction.dark} !important`,
                            "&:hover": {
                              backgroundColor: theme.palette.callToAction.dark,
                            },
                          },
                          "&:not(:first-of-type)": {
                            borderLeft: `2px solid ${theme.palette.callToAction.main} !important`,
                            marginLeft: "-2px !important",
                          },
                          "&:first-of-type": {
                            borderTopLeftRadius: "8px !important",
                            borderBottomLeftRadius: "8px !important",
                          },
                          "&:last-of-type": {
                            borderTopRightRadius: "8px !important",
                            borderBottomRightRadius: "8px !important",
                          },
                        }}
                      >
                        {category}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  {editTags.includes(t`Smut`) && (
                    <Typography
                      variant="caption"
                      color="warning.main"
                      sx={{ mt: theme.spacing(1), textAlign: "center" }}
                    >
                      {t`Stories in category "Smut" might not be approperiate for all users.`}
                    </Typography>
                  )}
                </Box>

              </>
            ) : (
              <>
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{ textAlign: "left", marginBottom: theme.spacing(2) }}
                >
                  {story.title}
                </Typography>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  sx={{
                    textAlign: "left",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  {story.description || t`No description available.`}
                </Typography>

                <Box sx={{ mt: theme.spacing(1) }}>
                  <Typography variant="body2" color="textSecondary">
                    {t`Tags:`} {story.tags && story.tags.length > 0 ? story.tags.join(", ") : t`None`}
                  </Typography>
                </Box>
              </>
            )}
          </Grid2>
        </Grid2>

        {isEditing && hasChanges && (
          <Box sx={{ mt: theme.spacing(2), ml: 1 }}>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
              {saving ? (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                  {t`Saving...`}
                </Box>
              ) : (
                t`Save Changes`
              )}
            </Button>
          </Box>
        )}

        {/* Menu button */}
        <Box sx={{ position: "absolute", top: 28, right: 0, zIndex: 2 }}>
          <IconButton onClick={handleOpenMenu} sx={{ color: theme.palette.text.primary }}>
            <MoreVertIcon />
          </IconButton>

          <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleCloseMenu}>
            <MenuItem onClick={handleEdit}>
              <Box display="flex" alignItems="center" gap={1}>
                <EditIcon sx={{ fontSize: 16, color: theme.palette.text.primary }} />
                <Typography variant="body2">{t`Edit`}</Typography>
              </Box>
            </MenuItem>

            {(hasPublishedChapters && !hasDelistedChapters) && (
              <MenuItem onClick={handleTogglePublishStory} disabled={publishing || fetchingStory}>
                <Box display="flex" alignItems="center" gap={1}>
                  {publishing || fetchingStory ? (
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  ) : (
                    story?.isPublished ? (
                      <>
                        <NotInterestedIcon sx={{ fontSize: 16, color: theme.palette.text.primary }} />
                        <Typography variant="body2">{t`Unpublish`}</Typography>
                      </>
                    ) : (
                      <>
                        <PublishIcon sx={{ fontSize: 16, color: theme.palette.text.primary }} />
                        <Typography variant="body2">{t`Publish`}</Typography>
                      </>
                    )
                  )}
                </Box>
              </MenuItem>

            )}

            <MenuItem>
              <Box display="flex" alignItems="center" gap={1}>
                {togglingActive && (
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                )}
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  disabled={togglingActive}
                  value={effectiveIsActive ? "active" : "finished"}
                  onChange={handleToggleActiveStatus}
                  sx={{
                    border: "none",
                    "& .MuiToggleButton-root": {
                      border: "none",
                      boxShadow: "none",
                    },
                    "& .MuiToggleButton-root.Mui-selected": {
                      border: "none",
                      boxShadow: "none",
                    },
                    "& .MuiToggleButton-root.Mui-disabled": {
                      border: "none",
                      backgroundColor: "transparent",
                      boxShadow: "none",
                    },
                  }}
                >
                  <ToggleButton value="active" disableRipple sx={{ textTransform: "none", border: "none" }}>
                    {t`Ongoing`}
                  </ToggleButton>
                  <ToggleButton value="finished" disableRipple sx={{ textTransform: "none", border: "none" }}>
                    {t`Finished`}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </MenuItem>


            <MenuItem onClick={handleOpenDeleteDialog}>
              <Box display="flex" alignItems="center" gap={1}>
                <DeleteIcon sx={{ fontSize: 16, color: theme.palette.text.primary }} />
                <Typography variant="body2">{t`Delete`}</Typography>
              </Box>
            </MenuItem>
          </Menu>

        </Box>
      </Box>

      <Divider sx={{ marginY: theme.spacing(3) }} />

      <Box mt={3}>
        <ChapterList
          storyID={storyID}
          chapters={story.chapters}
          onDelete={handleDeleteChapter}
          onChapterPublished={handleChapterPublished}
        />
      </Box>

      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        fullWidth
        sx={{ marginTop: theme.spacing(3) }}
        onClick={handleAddChapter}
      >
        {t`Add New Chapter`}
      </Button>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default StoryDetailsPage;
